import { z } from 'zod';
import { Decimal } from '@sarha/payroll';
import {
  EMPLOYEE_STATUSES,
  EMPLOYEE_TYPES,
  FUEL_POLICIES,
  FISCAL_STATUSES,
  LIMITS,
  OWNERSHIP_TYPES,
  PAYMENT_METHODS,
  ROLES,
  VEHICLE_STATUSES,
  DELIVERY_PLATFORMS,
  VEHICLE_TYPES,
} from './constants';

// ═══════════════════════════════════════════════════════════════════════════
//  أدوات مساعدة للتحقق
// ═══════════════════════════════════════════════════════════════════════════

const AR = {
  required: 'هذا الحقل مطلوب',
  invalid: 'قيمة غير صحيحة',
  tooShort: (n: number) => `يجب ألا يقل عن ${n} أحرف`,
  tooLong: (n: number) => `يجب ألا يزيد عن ${n} حرفاً`,
};

/** الحد الأقصى للمبلغ حسب Decimal(12,2) */
const MONEY_LIMIT = new Decimal('9999999999.99');
const MONEY_TEXT = /^-?\d+(\.\d+)?$/;

/**
 * تحليل آمن لمبلغ: يُعيد Decimal أو null — **لا يرمي استثناءً أبداً**
 * حتى لا يتحول مدخل غير صالح إلى خطأ 500 بدل خطأ تحقق 400.
 */
function parseMoney(value: unknown): Decimal | null {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (!MONEY_TEXT.test(text)) return null;
  try {
    const parsed = new Decimal(text);
    return parsed.isFinite() ? parsed : null;
  } catch {
    return null;
  }
}

function isNegativeMoney(value: unknown): boolean {
  const parsed = parseMoney(value);
  return parsed !== null && parsed.isNegative();
}

function isPositiveMoney(value: unknown): boolean {
  const parsed = parseMoney(value);
  return parsed !== null && parsed.gt(0);
}

/** مبلغ مالي: يُقبل رقم أو نص، ويُطبَّع إلى نص بمنزلتين عشريتين (ROUND_HALF_UP) */
export const moneySchema = z
  .union([z.number(), z.string()])
  .superRefine((value, ctx) => {
    const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
    if (text === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: AR.required });
      return;
    }
    const parsed = parseMoney(value);
    if (!parsed) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'أدخل مبلغاً رقمياً صحيحاً' });
      return;
    }
    if (parsed.abs().gt(MONEY_LIMIT)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'المبلغ يتجاوز الحد الأقصى المسموح' });
    }
  })
  .transform((value) => {
    const parsed = parseMoney(value);
    // القيم غير الصالحة تُترك كما هي ليُبلَّغ عنها في superRefine (لا نرمي استثناءً هنا أبداً)
    if (!parsed) return typeof value === 'string' ? value : '';
    return parsed.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
  });

/** مبلغ غير سالب */
export const amountSchema = moneySchema.refine((v) => !isNegativeMoney(v), {
  message: 'المبلغ لا يمكن أن يكون سالباً',
});

/** مبلغ أكبر من صفر */
export const positiveAmountSchema = amountSchema.refine((v) => isPositiveMoney(v), {
  message: 'يجب أن يكون المبلغ أكبر من صفر',
});

/** نسبة مئوية 0..100 */
export const percentSchema = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === 'number' ? String(v) : v.trim()))
  .refine((v) => /^\d+(\.\d{1,2})?$/.test(v) && Number(v) <= 100, {
    message: 'النسبة يجب أن تكون بين 0 و 100',
  });

/** تاريخ: يقبل YYYY-MM-DD أو ISO كامل */
export const dateSchema = z.coerce.date({
  required_error: 'التاريخ مطلوب',
  invalid_type_error: 'تاريخ غير صحيح',
});

/** شهر YYYY-MM */
export const monthSchema = z
  .string({ required_error: 'الشهر مطلوب' })
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'صيغة الشهر يجب أن تكون YYYY-MM');

/** رقم صحيح غير سالب */
export const countSchema = z.coerce
  .number({ invalid_type_error: 'أدخل رقماً صحيحاً' })
  .int('أدخل رقماً صحيحاً')
  .min(0, 'لا يمكن أن يكون سالباً')
  .max(100000, 'القيمة كبيرة جداً');

/** رقم الإقامة: 10 أرقام بالضبط */
export const iqamaSchema = z
  .string({ required_error: 'رقم الإقامة مطلوب' })
  .trim()
  .regex(/^\d{10}$/, 'رقم الإقامة يجب أن يكون 10 أرقام');

/** الجوال السعودي: 05xxxxxxxx أو +9665xxxxxxxx — يُطبَّع إلى 05xxxxxxxx */
export const phoneSchema = z
  .string({ required_error: 'رقم الجوال مطلوب' })
  .trim()
  .regex(/^(05\d{8}|\+9665\d{8}|9665\d{8})$/, 'رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام')
  .transform((v) => (v.startsWith('05') ? v : `0${v.slice(-9)}`));

/** الآيبان السعودي: SA + 22 رقماً */
export const ibanSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, '').toUpperCase())
  .refine((v) => v === '' || /^SA\d{22}$/.test(v), {
    message: 'الآيبان يجب أن يبدأ بـ SA ويتكون من 24 خانة',
  })
  .transform((v) => (v === '' ? null : v));

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, AR.tooLong(max))
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === '' ? null : v));

/** معامل منطقي في query string */
export const boolQuery = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .optional()
  .transform((v) => v === true || v === 'true' || v === '1');

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(LIMITS.maxPageSize).default(LIMITS.defaultPageSize),
  search: z.string().trim().max(120).optional(),
  includeDeleted: boolQuery,
  sortBy: z.string().trim().max(40).optional(),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
});
export type Pagination = z.infer<typeof paginationSchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  المصادقة والمستخدمون
// ═══════════════════════════════════════════════════════════════════════════

export const loginSchema = z.object({
  email: z.string({ required_error: 'البريد الإلكتروني مطلوب' }).trim().toLowerCase().email('صيغة البريد الإلكتروني غير صحيحة'),
  password: z.string({ required_error: 'كلمة المرور مطلوبة' }).min(1, 'كلمة المرور مطلوبة'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'كلمة المرور الحالية مطلوبة'),
    newPassword: z
      .string()
      .min(LIMITS.minPasswordLength, AR.tooShort(LIMITS.minPasswordLength))
      .max(128, AR.tooLong(128))
      .regex(/[A-Za-z]/, 'يجب أن تحتوي على حرف واحد على الأقل')
      .regex(/\d/, 'يجب أن تحتوي على رقم واحد على الأقل'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'كلمتا المرور غير متطابقتين',
    path: ['confirmPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const userCreateSchema = z.object({
  name: z.string({ required_error: 'الاسم مطلوب' }).trim().min(2, AR.tooShort(2)).max(120),
  email: z.string().trim().toLowerCase().email('صيغة البريد الإلكتروني غير صحيحة'),
  password: z
    .string()
    .min(LIMITS.minPasswordLength, AR.tooShort(LIMITS.minPasswordLength))
    .max(128)
    .regex(/[A-Za-z]/, 'يجب أن تحتوي على حرف واحد على الأقل')
    .regex(/\d/, 'يجب أن تحتوي على رقم واحد على الأقل'),
  role: z.enum(ROLES, { errorMap: () => ({ message: 'الدور غير صحيح' }) }),
  isActive: z.boolean().default(true),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z.object({
  name: z.string().trim().min(2, AR.tooShort(2)).max(120).optional(),
  email: z.string().trim().toLowerCase().email('صيغة البريد الإلكتروني غير صحيحة').optional(),
  role: z.enum(ROLES).optional(),
  isActive: z.boolean().optional(),
  password: z
    .string()
    .min(LIMITS.minPasswordLength, AR.tooShort(LIMITS.minPasswordLength))
    .max(128)
    .optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  الموظفون (السائقون)
// ═══════════════════════════════════════════════════════════════════════════

export const employeeCreateSchema = z.object({
  code: z.string().trim().min(1, 'الرقم الوظيفي مطلوب').max(30).optional(),
  fullName: z.string({ required_error: 'اسم السائق مطلوب' }).trim().min(3, AR.tooShort(3)).max(150),
  iqamaNumber: iqamaSchema,
  iqamaExpiry: dateSchema,
  nationality: z.string({ required_error: 'الجنسية مطلوبة' }).trim().min(2, AR.tooShort(2)).max(60),
  phone: phoneSchema.optional().nullable().or(z.literal('').transform(() => null)),
  jobTitle: optionalText(80),
  iban: ibanSchema.optional(),
  appUsername: optionalText(80),
  licenseNumber: optionalText(40),
  licenseExpiry: dateSchema.optional().nullable(),
  passportNumber: optionalText(40),
  passportExpiry: dateSchema.optional().nullable(),
  vehicleId: z.string().trim().min(1).optional().nullable(),
  hireDate: dateSchema,
  endDate: dateSchema.optional().nullable(),
  status: z.enum(EMPLOYEE_STATUSES).default('active'),
  employeeType: z.enum(EMPLOYEE_TYPES).default('driver'),
  baseSalary: amountSchema.default('0.00'),
  housingAllowance: amountSchema.default('0.00'),
  transportAllowance: amountSchema.default('0.00'),
  orderRate: amountSchema.default('0.00'),
  internetFee: amountSchema.default('0.00'),
  fuelPolicy: z.enum(FUEL_POLICIES).default('company'),
  // ── نظام التاركت ──
  earnsPerOrder: z.boolean().default(true),
  orderTarget: z.coerce.number().int().min(0).max(100000).optional().nullable(),
  orderRateWithin: amountSchema.optional().nullable(),
  orderRateAbove: amountSchema.optional().nullable(),
  platforms: z
    .array(
      z.object({
        platform: z.enum(DELIVERY_PLATFORMS),
        riderId: z.string().trim().min(1).max(60),
      }),
    )
    .max(10)
    .optional(),
  notes: optionalText(1000),
});
export type EmployeeCreateInput = z.infer<typeof employeeCreateSchema>;

export const employeeUpdateSchema = employeeCreateSchema.partial();
export type EmployeeUpdateInput = z.infer<typeof employeeUpdateSchema>;

/** إعداد تاركت الشهر: الهدف الموحّد + أسعار كل نوع مركبة */
export const payrollTargetSchema = z.object({
  periodMonth: monthSchema,
  targetOrders: z.coerce.number().int().min(0).max(100000),
  rates: z
    .array(
      z.object({
        vehicleType: z.enum(VEHICLE_TYPES),
        rateWithin: amountSchema,
        rateAbove: amountSchema,
      }),
    )
    .default([]),
});
export type PayrollTargetInput = z.infer<typeof payrollTargetSchema>;

export const employeeQuerySchema = paginationSchema.extend({
  status: z.enum(EMPLOYEE_STATUSES).optional(),
  employeeType: z.enum(EMPLOYEE_TYPES).optional(),
  vehicleId: z.string().trim().optional(),
  iqamaExpiringBefore: dateSchema.optional(),
  licenseExpiringBefore: dateSchema.optional(),
});
export type EmployeeQuery = z.infer<typeof employeeQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  الدراجات
// ═══════════════════════════════════════════════════════════════════════════

export const vehicleCreateSchema = z.object({
  plateNumber: z.string({ required_error: 'رقم اللوحة مطلوب' }).trim().min(2, AR.tooShort(2)).max(30),
  make: z.string({ required_error: 'الصنع مطلوب' }).trim().min(1, 'الصنع مطلوب').max(60),
  model: z.string({ required_error: 'الطراز مطلوب' }).trim().min(1, 'الطراز مطلوب').max(60),
  year: z.coerce.number().int().min(1990, 'سنة الصنع غير صحيحة').max(2100, 'سنة الصنع غير صحيحة'),
  registrationExpiry: dateSchema,
  insuranceExpiry: dateSchema,
  inspectionExpiry: dateSchema,
  odometer: countSchema.default(0),
  ownership: z.enum(OWNERSHIP_TYPES).default('owned'),
  vehicleType: z.enum(VEHICLE_TYPES).default('motorcycle'),
  monthlyLease: amountSchema.default('0.00'),
  status: z.enum(VEHICLE_STATUSES).default('active'),
  lastServiceDate: dateSchema.optional().nullable(),
  lastServiceOdometer: countSchema.optional().nullable(),
  serviceIntervalKm: countSchema.optional().nullable(),
  notes: optionalText(1000),
});
export type VehicleCreateInput = z.infer<typeof vehicleCreateSchema>;

export const vehicleUpdateSchema = vehicleCreateSchema.partial();
export type VehicleUpdateInput = z.infer<typeof vehicleUpdateSchema>;

export const vehicleQuerySchema = paginationSchema.extend({
  status: z.enum(VEHICLE_STATUSES).optional(),
  ownership: z.enum(OWNERSHIP_TYPES).optional(),
  vehicleType: z.enum(VEHICLE_TYPES).optional(),
});
export type VehicleQuery = z.infer<typeof vehicleQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  السجل اليومي (يدعم الإدخال الجماعي والمزامنة offline)
// ═══════════════════════════════════════════════════════════════════════════

export const dailyEntryRowSchema = z.object({
  employeeId: z.string().trim().min(1, 'السائق مطلوب'),
  ordersCount: countSchema.default(0),
  cashCollected: amountSchema.default('0.00'),
  cashDeposited: amountSchema.default('0.00'),
  workedHours: z
    .union([z.number(), z.string()])
    .optional()
    .transform((v) => {
      const n = v === undefined || v === '' ? 0 : Number(v);
      return Number.isFinite(n) && n >= 0 ? new Decimal(n).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2) : '0.00';
    }),
  notes: optionalText(300),
  /** مفتاح idempotency من الواجهة — يمنع التكرار عند المزامنة المتكررة */
  clientUuid: z.string().trim().max(64).optional().nullable(),
  /** آخر تعديل محلي (لحل التعارض: آخر كتابة تفوز) */
  clientUpdatedAt: z.coerce.date().optional().nullable(),
});
export type DailyEntryRowInput = z.infer<typeof dailyEntryRowSchema>;

export const dailyBulkSchema = z.object({
  date: dateSchema,
  rows: z.array(dailyEntryRowSchema).max(500, 'عدد الصفوف كبير جداً'),
});
export type DailyBulkInput = z.infer<typeof dailyBulkSchema>;

export const dailySyncSchema = z.object({
  entries: z
    .array(
      dailyEntryRowSchema.extend({
        date: dateSchema,
        clientUuid: z.string().trim().min(8, 'معرّف المزامنة مطلوب').max(64),
      }),
    )
    .max(1000, 'حجم الدفعة كبير جداً'),
});
export type DailySyncInput = z.infer<typeof dailySyncSchema>;

export const dailyQuerySchema = z.object({
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  month: monthSchema.optional(),
  employeeId: z.string().trim().optional(),
  includeDeleted: boolQuery,
});
export type DailyQuery = z.infer<typeof dailyQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  الرواتب
// ═══════════════════════════════════════════════════════════════════════════

export const adjustmentCreateSchema = z.object({
  periodMonth: monthSchema,
  employeeId: z.string({ required_error: 'السائق مطلوب' }).trim().min(1, 'السائق مطلوب'),
  kind: z.enum(['earning', 'deduction'], { errorMap: () => ({ message: 'نوع البند غير صحيح' }) }),
  category: z.string({ required_error: 'التصنيف مطلوب' }).trim().min(1, 'التصنيف مطلوب').max(40),
  amount: positiveAmountSchema,
  description: optionalText(300),
});
export type AdjustmentCreateInput = z.infer<typeof adjustmentCreateSchema>;

export const adjustmentUpdateSchema = z.object({
  category: z.string().trim().min(1).max(40).optional(),
  kind: z.enum(['earning', 'deduction']).optional(),
  amount: positiveAmountSchema.optional(),
  description: optionalText(300),
});
export type AdjustmentUpdateInput = z.infer<typeof adjustmentUpdateSchema>;

export const payrollPaySchema = z.object({
  paidAt: dateSchema,
  paymentMethod: z.enum(PAYMENT_METHODS).default('bank'),
  notes: optionalText(500),
});
export type PayrollPayInput = z.infer<typeof payrollPaySchema>;

export const payrollUnlockSchema = z.object({
  reason: z.string({ required_error: 'سبب الفتح مطلوب' }).trim().min(10, 'اذكر سبباً واضحاً (10 أحرف على الأقل)').max(500),
});
export type PayrollUnlockInput = z.infer<typeof payrollUnlockSchema>;

export const bankFileQuerySchema = z.object({
  format: z.enum(['csv']).default('csv'),
  onlyWithIban: boolQuery,
});

// ═══════════════════════════════════════════════════════════════════════════
//  المصروفات والإيرادات
// ═══════════════════════════════════════════════════════════════════════════

export const expenseCreateSchema = z.object({
  date: dateSchema,
  categoryId: z.string({ required_error: 'التصنيف مطلوب' }).trim().min(1, 'التصنيف مطلوب'),
  description: z.string({ required_error: 'البيان مطلوب' }).trim().min(2, AR.tooShort(2)).max(300),
  amount: positiveAmountSchema,
  vatIncluded: z.boolean().default(true),
  vatAmount: moneySchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  supplierId: z.string().trim().min(1).optional().nullable(),
  invoiceNumber: optionalText(60),
  vehicleId: z.string().trim().min(1).optional().nullable(),
  employeeId: z.string().trim().min(1).optional().nullable(),
  attachmentId: z.string().trim().min(1).optional().nullable(),
  notes: optionalText(500),
});
export type ExpenseCreateInput = z.infer<typeof expenseCreateSchema>;

export const expenseUpdateSchema = expenseCreateSchema.partial();
export type ExpenseUpdateInput = z.infer<typeof expenseUpdateSchema>;

export const expenseQuerySchema = paginationSchema.extend({
  month: monthSchema.optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  categoryId: z.string().trim().optional(),
  vehicleId: z.string().trim().optional(),
  supplierId: z.string().trim().optional(),
  employeeId: z.string().trim().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  minAmount: moneySchema.optional(),
  maxAmount: moneySchema.optional(),
});
export type ExpenseQuery = z.infer<typeof expenseQuerySchema>;

export const expenseCategorySchema = z.object({
  nameAr: z.string({ required_error: 'اسم التصنيف مطلوب' }).trim().min(2, AR.tooShort(2)).max(80),
  parentId: z.string().trim().min(1).optional().nullable(),
  isActive: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});
export type ExpenseCategoryInput = z.infer<typeof expenseCategorySchema>;

export const supplierSchema = z.object({
  name: z.string({ required_error: 'اسم المورد مطلوب' }).trim().min(2, AR.tooShort(2)).max(120),
  phone: z.string().trim().max(20).optional().nullable(),
  vatNumber: z
    .string()
    .trim()
    .max(20)
    .optional()
    .nullable()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  notes: optionalText(500),
});
export type SupplierInput = z.infer<typeof supplierSchema>;

export const revenueCreateSchema = z.object({
  date: dateSchema,
  source: z.string({ required_error: 'المصدر مطلوب' }).trim().min(2, AR.tooShort(2)).max(80),
  ordersCount: countSchema.default(0),
  amount: positiveAmountSchema,
  vatIncluded: z.boolean().default(true),
  vatAmount: moneySchema.optional(),
  reference: optionalText(60),
  notes: optionalText(500),
});
export type RevenueCreateInput = z.infer<typeof revenueCreateSchema>;

export const revenueUpdateSchema = revenueCreateSchema.partial();
export type RevenueUpdateInput = z.infer<typeof revenueUpdateSchema>;

export const revenueQuerySchema = paginationSchema.extend({
  month: monthSchema.optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  source: z.string().trim().optional(),
});
export type RevenueQuery = z.infer<typeof revenueQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  الإعدادات والسجل الرقابي والإقفال السنوي
// ═══════════════════════════════════════════════════════════════════════════

export const settingsUpdateSchema = z.object({
  companyName: z.string().trim().min(2, AR.tooShort(2)).max(150).optional(),
  crNumber: optionalText(30),
  unifiedCrNumber: optionalText(30),
  vatNumber: optionalText(20),
  address: optionalText(200),
  currency: z.string().trim().length(3).optional(),
  vatRate: percentSchema.optional(),
  defaultOrderRate: amountSchema.optional(),
  revenuePerOrder: amountSchema.optional(),
  iqamaAlertDays: z.coerce.number().int().min(1).max(365).optional(),
  fiscalStartMonth: z.coerce.number().int().min(1).max(12).optional(),
  employeeCodePrefix: z.string().trim().min(1).max(10).optional(),
  prorationMode: z.enum(['none', 'calendarDays', 'workedDays']).optional(),
  workHoursPerDay: z.coerce.number().min(1).max(24).optional(),
});
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;

export const auditQuerySchema = paginationSchema.extend({
  entity: z.string().trim().optional(),
  entityId: z.string().trim().optional(),
  userId: z.string().trim().optional(),
  action: z.string().trim().optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;

export const reportQuerySchema = z.object({
  kind: z.enum([
    'driver-profitability',
    'daily-orders-avg',
    'fuel-consumption',
    'expenses-by-category',
    'month-comparison',
    'vat-report',
    'payroll-register',
    'cash-custody',
    'annual-closing',
  ]),
  month: monthSchema.optional(),
  from: dateSchema.optional(),
  to: dateSchema.optional(),
  compareMonth: monthSchema.optional(),
  vehicleId: z.string().trim().optional(),
  employeeId: z.string().trim().optional(),
  categoryId: z.string().trim().optional(),
  format: z.enum(['json', 'csv', 'pdf']).default('json'),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type ReportQuery = z.infer<typeof reportQuerySchema>;

export const fiscalCloseSchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  notes: optionalText(500),
});
export type FiscalCloseInput = z.infer<typeof fiscalCloseSchema>;

export const exportQuerySchema = z.object({
  format: z.enum(['json', 'csv']).default('json'),
  tables: z.string().trim().optional(),
  month: monthSchema.optional(),
});
export type ExportQuery = z.infer<typeof exportQuerySchema>;

export const fiscalQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  status: z.enum(FISCAL_STATUSES).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
//  استيراد ملفات Excel / CSV
// ═══════════════════════════════════════════════════════════════════════════

/** أنواع البيانات المدعومة في الاستيراد التلقائي */
export const IMPORT_TYPES = ['employees', 'vehicles', 'expenses', 'revenues', 'daily_entries'] as const;
export type ImportType = (typeof IMPORT_TYPES)[number];

export const IMPORT_TYPE_LABELS: Record<ImportType, string> = {
  employees: 'العمال / السائقون',
  vehicles: 'الدراجات والمركبات',
  expenses: 'مصروفات الشركة',
  revenues: 'الإيرادات',
  daily_entries: 'السجل اليومي',
};

export const importTypeSchema = z.enum(IMPORT_TYPES, {
  errorMap: () => ({ message: 'نوع الاستيراد غير مدعوم' }),
});

const importDefaultDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'تاريخ السجل غير صحيح (YYYY-MM-DD)');

export const importCommitSchema = z.object({
  token: z
    .string({ required_error: 'رمز الاستيراد مطلوب' })
    .trim()
    .regex(/^[0-9a-f-]{36}\.(xlsx|xls|csv)$/i, 'رمز الاستيراد غير صالح — أعد رفع الملف'),
  type: importTypeSchema,
  skipErrors: z.boolean().default(true),
  /** تاريخ يُطبَّق على كل الصفوف حين يخلو الملف من عمود التاريخ (تقارير الفترة من المنصات) */
  defaultDate: importDefaultDateSchema.optional(),
});
export type ImportCommitInput = z.infer<typeof importCommitSchema>;

export const importPreviewQuerySchema = z.object({
  type: importTypeSchema.optional(),
  defaultDate: importDefaultDateSchema.optional(),
});
export type ImportPreviewQuery = z.infer<typeof importPreviewQuerySchema>;

export const importHistoryQuerySchema = paginationSchema;
export type ImportHistoryQuery = z.infer<typeof importHistoryQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
//  أشكال الاستجابة الموحّدة
// ═══════════════════════════════════════════════════════════════════════════

export interface ApiErrorBody {
  error: {
    code: string;
    messageAr: string;
    fields?: Array<{ path: string; message: string }>;
    details?: unknown;
  };
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}
